import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';
import { makeStyles } from '@material-ui/core/styles';

// Redux
import { connect } from 'react-redux';
import { setAlert } from '../../../redux/actions/alertActions';

// Server
import { listAccessSessions, getAccessSession } from '../../../api';

// Components
import PageHeader from '../../../components/PageHeader';
import StatCard from '../../../components/StatCard';
import { PillTrack, Pill } from '../../../components/Pill';
import { ListNote } from '../../../components/ListControls';

// Constants
import { UserTypes } from '../../../constants';
import { T, FONT_MONO, INDICATOR, INDICATOR_TEXT } from '../../../constants/tokens';

const REFRESH_MS = 30 * 1000;

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'ended', label: 'Signed out' }
];

// The design's columns: user, signed in, how it ended, duration, how much was
// done, and the last thing done.
const GRID = 'minmax(200px,1.3fr) 88px 170px 80px 170px minmax(200px,1.4fr)';

// "How much the person did": the design's bands, and the count that fills the bar.
const LEVELS = [{ min: 60, label: 'High' }, { min: 15, label: 'Medium' }, { min: 0, label: 'Low' }];
const FULL_BAR = 150;

const eyebrow = { fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body };

const useStyles = makeStyles({
  row: {
    display: 'grid',
    gridTemplateColumns: GRID,
    gap: 16,
    alignItems: 'center',
    width: '100%',
    padding: '14px 24px',
    border: 0,
    borderBottom: '1px solid ' + T.hairlineSoft,
    textAlign: 'left',
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:hover': { background: T.surfaceSoft },
    '&:focus-visible': { outline: '2px solid ' + T.focus, outlineOffset: -2 }
  },
  close: {
    fontFamily: 'inherit',
    width: 32,
    height: 32,
    border: '1px solid ' + T.hairline,
    borderRadius: 6,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    padding: 0,
    '&:hover': { background: T.surfaceSoft }
  }
});

const time = (iso) => (iso ? DateTime.fromISO(iso).toLocal().toLocaleString(DateTime.TIME_SIMPLE) : '—');

/** "2h 39m", "30m", "45s". */
const duration = (seconds) => {
  const s = Math.max(0, Math.round(seconds ?? 0));
  if (s < 60) return s + 's';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm';
  return Math.floor(m / 60) + 'h ' + String(m % 60).padStart(2, '0') + 'm';
};

/** How a session ended, as the design words and colours it. */
const outcome = (session) => {
  if (session.state === 'active') return { label: 'Active now', dot: INDICATOR.success, fg: INDICATOR_TEXT.success };
  if (session.state === 'timed_out') return { label: 'Timed out ' + time(session.endedAt), dot: INDICATOR.warning, fg: INDICATOR_TEXT.warning };
  return { label: 'Signed out ' + time(session.endedAt), dot: T.body, fg: T.body };
};

const roleOf = (session) => session.user.userType ?? 'User';

/**
 * The Access log: who is signed in now, who signed in today and how each
 * session ended, how much was done in it, and, for one person, everything they
 * did in order. Super users only.
 *
 * "Today" is this browser's day. A session times out a fixed time after
 * sign-in (the Logout Timer setting), as the sign-in cookie does. Sessions are
 * kept 90 days.
 */
function AccessLog({ userType, setError }) {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('all');
  const [selectedID, setSelectedID] = useState(null);
  const [selected, setSelected] = useState(null);
  const mounted = useRef(true);
  const trailRef = useRef(null);

  const isSuper = userType === UserTypes.SUPER || userType?.name === UserTypes.SUPER.name;

  const load = useCallback(() => {
    const from = DateTime.now().startOf('day').toJSDate();
    listAccessSessions({ from, to: new Date() })
      .then((result) => {
        if (mounted.current) setData(result);
      })
      .catch((error) => setError(error ? error : 'Could not retrieve the access log.'));
  }, [setError]);

  useEffect(() => {
    mounted.current = true;
    if (!isSuper) return undefined;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [isSuper, load]);

  // The chosen person's trail, kept current while they are active.
  useEffect(() => {
    if (selectedID == null) {
      setSelected(null);
      return undefined;
    }
    let current = true;
    const fetchTrail = () => getAccessSession(selectedID)
      .then((result) => {
        if (current) setSelected(result);
      })
      .catch((error) => setError(error ? error : 'Could not retrieve the session.'));
    fetchTrail();
    const timer = setInterval(fetchTrail, REFRESH_MS);
    return () => {
      current = false;
      clearInterval(timer);
    };
  }, [selectedID, setError]);

  // Bring a newly chosen trail into view (not on every refresh of it).
  const shownID = selected?.id ?? null;
  useEffect(() => {
    if (shownID != null && trailRef.current) trailRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [shownID]);

  if (!isSuper) return <Redirect to='/admin' />;

  const sessions = data?.sessions ?? [];
  const rows = sessions.filter((s) => tab === 'all' || (tab === 'active' ? s.state === 'active' : s.state !== 'active'));
  const stats = data?.stats;
  const since = DateTime.now().startOf('day').toLocaleString(DateTime.TIME_SIMPLE);

  return (
    <>
      <PageHeader
        title='Access log'
        subtitle={'Who signed in, and what they did' + (data ? ' · kept ' + data.retentionDays + ' days' : '')}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, marginBottom: 48 }}>
        <StatCard
          label='Signed in now'
          value={stats ? String(stats.signedInNow) : '—'}
          detail='Active sessions right now'
          accent={INDICATOR_TEXT.success}
        />
        <StatCard
          label='Sign-ins today'
          value={stats ? String(stats.signIns) : '—'}
          detail={stats ? stats.people + (stats.people === 1 ? ' person' : ' people') + ' · since ' + since : ''}
          accent={INDICATOR_TEXT.accent}
        />
        <StatCard
          label='Timed out'
          value={stats ? String(stats.timedOut) : '—'}
          detail='Left without signing out'
          accent={stats && stats.timedOut > 0 ? INDICATOR_TEXT.warning : undefined}
        />
      </div>

      <section style={{ background: T.elevated, border: '1px solid ' + T.hairline, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: 24, borderBottom: '1px solid ' + T.hairlineSoft }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, lineHeight: '28px', letterSpacing: '-0.4px', color: T.ink }}>Sessions today</h2>
          <div style={{ flex: '1 0 12px' }} />
          <PillTrack>
            {TABS.map((t) => <Pill key={t.key} on={tab === t.key} onClick={() => setTab(t.key)}>{t.label}</Pill>)}
          </PillTrack>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 940 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 16, padding: '12px 24px', background: T.surfaceSoft, borderBottom: '1px solid ' + T.hairline }}>
              {['User', 'Signed in', 'Signed out', 'Duration', 'Interaction', 'Last action'].map((c) => <span key={c} style={eyebrow}>{c}</span>)}
            </div>

            {data == null && <div style={{ padding: 24 }}><ListNote>Loading…</ListNote></div>}
            {data != null && rows.length === 0 &&
              <div style={{ padding: 24, fontSize: 14, color: T.muted }}>No sessions {tab === 'active' ? 'active now' : (tab === 'ended' ? 'ended today' : 'today')}.</div>}

            {rows.map((s) => {
              const end = outcome(s);
              const level = LEVELS.find((l) => s.actionCount >= l.min).label;
              return (
                <button
                  type='button'
                  key={s.id}
                  className={classes.row}
                  style={{ background: selectedID === s.id ? T.surfaceSoft : 'transparent' }}
                  onClick={() => setSelectedID(selectedID === s.id ? null : s.id)}
                  aria-pressed={selectedID === s.id}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 500, lineHeight: '20px', color: T.ink }}>{s.user.name || s.user.email || 'Unknown user'}</span>
                    <span style={{ display: 'block', fontSize: 12, lineHeight: '16px', color: T.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[roleOf(s), s.client].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 14, color: T.ink }}>{time(s.startedAt)}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, lineHeight: '20px', color: end.fg }}>
                    <span style={{ width: 8, height: 8, borderRadius: 9999, flex: 'none', background: end.dot }} />{end.label}
                  </span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 14, color: T.ink }}>{duration(s.durationSeconds)}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', height: 6, borderRadius: 9999, background: T.hairlineSoft, overflow: 'hidden' }}>
                      <span style={{ display: 'block', height: '100%', width: Math.min(100, Math.round(s.actionCount / FULL_BAR * 100)) + '%', background: T.ink }} />
                    </span>
                    <span style={{ display: 'block', marginTop: 6, fontFamily: FONT_MONO, fontSize: 12, color: T.body }}>
                      {s.actionCount} {s.actionCount === 1 ? 'action' : 'actions'} · {level}
                    </span>
                  </span>
                  <span style={{ fontSize: 14, lineHeight: '20px', color: T.body, overflowWrap: 'anywhere' }}>{s.lastAction?.text ?? '—'}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {selected &&
        <section ref={trailRef} style={{ marginTop: 24, background: T.elevated, border: '1px solid ' + T.hairline, borderRadius: 6, padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, lineHeight: '28px', letterSpacing: '-0.4px', color: T.ink }}>
                {selected.user.name || selected.user.email || 'Unknown user'}
              </h2>
              <div style={{ fontSize: 14, lineHeight: '20px', color: T.body, overflowWrap: 'anywhere' }}>
                {[roleOf(selected), selected.user.email, selected.client].filter(Boolean).join(' · ')}
              </div>
            </div>
            <button type='button' aria-label='Close' className={classes.close} onClick={() => setSelectedID(null)}>&#10005;</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selected.trail.map((entry) => (
              <div key={entry.id} style={{ display: 'grid', gridTemplateColumns: '64px 8px minmax(0,1fr)', gap: 12, alignItems: 'start' }}>
                <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.body, paddingTop: 2 }}>{time(entry.timestamp)}</span>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: entry.kind === 'view' ? T.mutedSoft : T.ink, marginTop: 6 }} />
                <span style={{ fontSize: 14, lineHeight: '20px', color: T.ink }}>{entry.text}</span>
              </div>
            ))}
          </div>
        </section>}
    </>
  );
}

AccessLog.propTypes = {
  userType: PropTypes.object,
  setError: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  userType: state.auth.userType
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg)
});

export default connect(mapStateToProps, mapDispatchToProps)(AccessLog);
